import { FC, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Link } from "@/components/Interface/Link/Link";
import { Notification } from "@/components/Interface/Notification/Notification";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { SubscriptionActionButton } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionActionButton/SubscriptionActionButton";
import { ChangeWatchSimRenewal } from "@/components/Portal/DeviceManagement/WatchSim/ChangeWatchSimRenewal/ChangeWatchSimRenewal";
import { WatchSimInfo } from "@/components/Portal/DeviceManagement/WatchSim/ShowWatchSimOptions/WatchSimInfo/WatchSimInfo";
import { WatchSimStatus } from "@/components/Portal/DeviceManagement/WatchSim/ShowWatchSimOptions/WatchSimStatus/WatchSimStatus";
import { useIsMobile } from "@/hooks/useIsMobile";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { SimCardDetail, WatchSimWatchStatus } from "@/services/model";
import {
  canBookWatchSim,
  hasWatchSim,
  hasWatchSimLocked,
  hasWatchSimPending,
  isWatchSimPausing,
} from "@/utils/deviceUtils";

/**
 * The "Watch SIM" tab of the device detail view - mirrors the "Subscription" tab's
 * split: the product/status cards on the left, the dynamic hint on the right.
 */
export const ShowWatchSimOptions: FC = () => {
  const { data: simDetail, isPending: isLoading } = useDisplayedSim();

  if (isLoading || !simDetail) {
    return <BrandLoadingScreen />;
  }

  // Mirrors WatchSimHint's own branching below: it renders nothing only for
  // a booked, already-active Watch SIM. Needed up front, as plain booleans
  // rather than the component's actual render, so the grid can skip
  // reserving a column it would otherwise leave empty (see Details).
  const hasHint =
    !hasWatchSim(simDetail) ||
    simDetail.watchSim?.watchStatus === WatchSimWatchStatus.ORDERED;

  return (
    <TwoColumnLayout
      rows={[
        {
          left: (
            <div className="flex w-full flex-col gap-gutter">
              <WatchSimSections simDetail={simDetail} />
            </div>
          ),
          right: hasHint && <WatchSimHint simDetail={simDetail} />,
        },
      ]}
    />
  );
};

const WatchSimSections = ({ simDetail }: { simDetail: SimCardDetail }) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim",
  });
  const { t: tDefault } = useTranslation();
  const isMobile = useIsMobile();
  // Same inline-on-desktop/routed-on-mobile split as the subscription pause panel -
  // see SubscriptionDisplay for why the effect below is needed too.
  const [isRenewalPanelOpen, setRenewalPanelOpen] = useState(false);
  useEffect(() => {
    if (isMobile) {
      setRenewalPanelOpen(false);
    }
  }, [isMobile]);
  const showRenewalPanel = !isMobile && isRenewalPanelOpen;

  const isBooked = hasWatchSim(simDetail);
  const canBook = canBookWatchSim(simDetail);
  // Same flag WatchSimStatus uses, so "Active / next bill" never sits next
  // to "Cancel pause". For ACTIVE the successor-description guess is too
  // laggy after checkout; for ORDERED the successor *is* how the backend
  // records a pause (see `isWatchSimPausing`).
  const isPausing = isWatchSimPausing(simDetail);
  // The renewal toggle only makes sense once the Watch SIM has actually
  // been booked - a still-pending order or a locked SIM has nothing to pause.
  // ORDERED is already booked (see WatchSimStatus), so pause stays available.
  const isChangeDisabled =
    hasWatchSimPending(simDetail) || hasWatchSimLocked(simDetail);

  return (
    <>
      <WatchSimInfo
        simDetail={simDetail}
        action={
          !isBooked && (
            <SubscriptionActionButton
              dataTestid="watch-sim-book"
              label={tDefault("common.buttons.bookOption")}
              disabledHint={
                !canBook ? t("display.voiceOptionNeeded") : undefined
              }
              target={`${portalRoutes.manageDevice.watchSim.checkout}?simId=${simDetail.simId}`}
            />
          )
        }
      />

      {isBooked &&
        (showRenewalPanel ? (
          <ChangeWatchSimRenewal onClose={() => setRenewalPanelOpen(false)} />
        ) : (
          <WatchSimStatus
            simDetail={simDetail}
            action={
              <SubscriptionActionButton
                dataTestid="watch-sim-pause"
                label={isPausing ? t("status.cancelPause") : t("status.pause")}
                disabledHint={
                  isChangeDisabled ? t("status.changeDisabled") : undefined
                }
                {...(isMobile
                  ? {
                      target: `${portalRoutes.manageDevice.watchSim.automaticRenewal}?simId=${simDetail.simId}`,
                    }
                  : { onClick: () => setRenewalPanelOpen(true) })}
              />
            }
          />
        ))}
    </>
  );
};

/**
 * The "Good to know" panel beside the cards. Not booked: explains what
 * booking requires. Booked: the smartwatch pairing instructions, but only
 * while the Watch SIM is ORDERED (booked, ready to pair) - once ACTIVE there
 * is nothing left to explain. The parent only mounts this when `hasHint`
 * (see above) agrees it will render something.
 */
const WatchSimHint = ({ simDetail }: { simDetail: SimCardDetail }) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim.display",
  });
  const { t: tDefault } = useTranslation();

  const isBooked = hasWatchSim(simDetail);

  if (isBooked) {
    if (simDetail.watchSim?.watchStatus !== WatchSimWatchStatus.ORDERED) {
      return null;
    }

    return (
      <Notification
        dataTestid="watch-sim-hint"
        title={tDefault("Content.goodToKnow")}
        description={
          <Trans
            t={t}
            i18nKey="linkHelpText"
            components={[
              <Link
                key="instruction-link"
                href={t("linkHelpUrl")}
                size="sm"
                target="_blank"
                rel="noreferrer"
              />,
            ]}
          />
        }
      />
    );
  }

  return (
    <Notification
      dataTestid="watch-sim-hint"
      title={tDefault("Content.goodToKnow")}
      description={t(
        canBookWatchSim(simDetail) ? "creationHelpText" : "voiceOptionNeeded",
      )}
    />
  );
};
